import { workPriority } from '../problem-work/work-model'
import type { CustomAgentTask } from '@axilon-apps/domains-ontology-ui-state'
import { priorityToTier, type SeverityTier } from '../primitives/severityTier'
import type { WorkPage } from '../../lib/work-page'
import {
  taskItemId,
  taskPriority,
  taskStatus,
  taskTitle
} from '../../pages/TasksPage/tasks-helpers'

/** One item on the chat home: an open task or problem the user can ask about. */
export interface OpenWorkItem {
  context?: import('@axilon-apps/domains-ontology-ui-state').WorkHandoffContext
  id: string
  kind: 'task' | 'problem'
  title: string
  tier: SeverityTier
  /** Pill text: the task priority or the problem status. */
  pill: string
}

/**
 * The few open items the chat home leads with, newest first. Users see their
 * own work page's items (tasks or problems); debug mode sees both.
 */
export function topOccurrenceWork(
  tasks: readonly import('@axilon-apps/domains-ontology-ui-state').ProblemTask[],
  problems: readonly import('@axilon-apps/domains-ontology-ui-state').ProblemOccurrence[],
  legacy: readonly CustomAgentTask[],
  pages: readonly WorkPage[],
  limit = 3
): OpenWorkItem[] {
  const items: (OpenWorkItem & { time: string })[] = []
  if (pages.includes('problems'))
    for (const problem of problems) {
      if (problem.status !== 'open') continue
      items.push({
        id: problem.id,
        kind: 'problem',
        title: problem.problem_name,
        tier: 'elevated',
        pill: 'problem',
        time: problem.opened_at,
        context: { source: 'occurrence', occurrenceId: problem.id, conceptId: problem.problem_id }
      })
    }
  if (pages.includes('tasks')) {
    for (const task of tasks) {
      if (task.retired_at || task.work_status !== 'open' || task.approval_status === 'rejected')
        continue
      const priority = workPriority(task.priority)
      items.push({
        id: task.id,
        kind: 'task',
        title: task.title,
        tier: priority ? priorityToTier(priority) : 'healthy',
        pill: priority ?? 'task',
        time: task.created_at,
        context: {
          source: 'occurrence',
          occurrenceId: task.occurrence_id,
          taskId: task.id,
          runId: task.producing_run_id
        }
      })
    }
    for (const task of legacy) {
      if (taskStatus(task) !== 'open') continue
      const priority = taskPriority(task)
      items.push({
        id: task.id,
        kind: 'task',
        title: taskTitle(task),
        tier: priority ? priorityToTier(priority) : 'healthy',
        pill: priority ?? 'task',
        time: task.time,
        context: {
          source: 'legacy_result',
          resultItemId: taskItemId(task),
          runId: task.run_id
        }
      })
    }
  }
  return items
    .sort((a, b) => b.time.localeCompare(a.time) || a.id.localeCompare(b.id))
    .slice(0, limit)
    .map(({ time: _time, ...item }) => item)
}
